<template>
  <div class="account-settings">
    <h1 class="settings-title">
      Change Phone
    </h1>
    <div class="settings-form">
      <div>
        <b-form-group>
          <label
            for="phone"
            class="settings-label">
            <span>Phone number</span>
            <icon
              id="message-notification-description"
              v-b-tooltip="'The phone number will be used for receiving WhatsApp notifications.'"
              name="info-circle"
              class="info-label"/>
          </label>
          <PhoneInput
            ref="phoneInput"
            v-model="phone"
            v-validate="'required'"
            name="phone"
            data-vv-as="Phone number"
            placeholder="0123456007"
            verify-tooltip="Phone number is not verified. You will not receive notifications untill you verify your number."
            @onInput="setPhoneObject"
            @verifyPhone="verifyPhone"/>
        </b-form-group>
        <span
          v-if="errors.has('phone')"
          class="validation-error">{{ errors.first('phone') }}</span>
        <span
          v-if="validationErrors.phone"
          class="validation-error">{{ validationErrors.phone }}</span>
      </div>
    </div>
    <div
      v-if="phoneUpdated"
      class="account-settings__success">
      Phone has been successfully updated.
    </div>
    <div
      v-if="errorMessage"
      class="account-settings__error">
      {{ errorMessage }}
    </div>
    <b-button
      :class="{loading: saving}"
      :disabled="saving || !phoneObject.isValid"
      class="account-settings__submit btn-round"
      @click="getConfirmation">
      <span v-if="!saving">Save</span>
      <div class="loader"><div class="loading-circle"/></div>
    </b-button>
    <b-button
      v-if="initialPhone"
      v-b-tooltip="'Deleting your phone will prevent receiving WhatsApp notification.'"
      :class="{loading: deletingPhoneInProgress}"
      :disabled="deletingPhoneInProgress"
      class="account-settings__submit btn-round ml-3 delete-btn"
      @click="deletePhoneBtnAction">
      <span v-if="!deletingPhoneInProgress">Delete</span>
      <div class="loader"><div class="loading-circle"/></div>
    </b-button>
    <VerifyPhoneModal
      v-if="phone"
      ref="verifyPhoneModal"
      :phone-number="phone"
      @verified="onVerify"/>
  </div>
</template>

<style lang="scss" scoped>


.delete-btn {
  background-color: transparent !important;
  border: none !important;
  color: $stateGray !important;

  &:hover, span:hover {
    background-color: transparent !important;
    color: $matteRed !important;
  }
}
</style>

<script>
import setValidationErrors from '@/helpers/validationErrors';
import { mapActions, mapGetters } from 'vuex';
import { createHelpers } from 'vuex-map-fields';
import VerifyPhoneModal from './modals/VerifyPhone.modal';
import PhoneInput from '../common/PhoneInput';

// ************* NOTE*****************
// variable successEmailUpdate is used because for now, phone and email are being set on the same endpoint and they have to be sent together
// even though there are no input fields for both on the same page.
// That is why we reuse successEmailUpdate event.

const { mapFields } = createHelpers({
  getterType: 'user/getField',
  mutationType: 'user/updateField',
});

export default {
  name: 'PhoneSettings',
  components: {
    VerifyPhoneModal,
    PhoneInput,
  },
  data() {
    return {
      password: '',
      errorMessage: '',
      saving: false,
      deleting: false,
      phoneUpdated: false,
      phoneUnchanged: true,
      validationErrors: {},
      initialPhone: '',
      initialPhoneSet: false,
      waitingForUpdate: false,
      phone2: '',
      phoneObject: {},
    };
  },
  computed: {
    ...mapGetters({
      userLoaded: 'user/userLoaded',
      successPhoneUpdate: 'user/successPhoneUpdate',
      apiError: 'user/apiError',
      phoneVerified: 'user/phoneVerified',
      phoneCountryCode: 'user/phoneCountryCode',
      deletingPhoneInProgress: 'user/deletingPhoneInProgress',
    }),
    ...mapFields([
      'phone',
    ]),
    phoneVerifiedTooltip() {
      return this.phoneVerified ? 'Phone number is verified.' : 'Phone number is not verified. You will not receive notifications untill you verify your number.';
    },
    showVerifiedStatus() {
      return (
        !this.waitingForUpdate &&
        this.phone &&
        ((this.veeFields && this.veeFields.phone && this.veeFields.phone.pristine) || this.phone === this.initialPhone)
      );
    },
    fieldsValid() {
      return this.phoneObject.valid;
    },
  },
  watch: {
    successPhoneUpdate(success) {
      if (success) {
        this.saving = false;
        this.phoneUpdated = true;
        setTimeout(() => {
          this.phoneUpdated = false;
        }, 3000);
        this.fetchUser();
      }
    },
    apiError(error) {
      this.saving = false;
      if (error.status === 422) {
        this.validationErrors = setValidationErrors(error.data.errors);
      } else if (error.status === 401) {
        this.errorMessage = 'Authentication failed. Wrong Password.';
      } else {
        this.errorMessage = error.data ? error.data.message : '';
      }
    },
    phone(newValue, oldValue) {
      if (oldValue && newValue && !this.initialPhoneSet) {
        this.initialPhoneSet = true;
        // If set via vuex then the length will be > 1 so accept the new value as initial value
        if (Math.abs(oldValue.length - newValue.length) === 1) {
          this.initialPhone = oldValue;
        } else {
          this.initialPhone = newValue;
        }
      }
    },
    userLoaded() {
      this.waitingForUpdate = false;
      this.$refs.phoneInput.setPhone(this.phoneCountryCode, this.phone, this.phoneVerified, true);
      this.initialPhone = this.phone;
      this.initialPhoneSet = true;
    },
    deletingPhoneInProgress(state) {
      if (!state) {
        this.fetchUser();
      }
    },
  },
  mounted() {
    // TODO when returning from notifications user is loaded, when returning from email user is not loaded
    // if (!this.userLoaded) {
    this.fetchUser();
    // }
  },
  beforeDestroy() {
    this.fetchUser();
  },
  methods: {
    ...mapActions({
      fetchUser: 'user/fetchUser',
      updatePhone: 'user/updatePhone',
      deletePhone: 'user/deletePhone',
    }),
    savePhone() {
      this.waitingForUpdate = true;
      this.saving = true;
      const payload = {
        phone: this.phoneObject.number,
        country_code: this.phoneObject.prefix,
      };
      this.updatePhone(payload);
      this.initialPhone = this.phone;
    },
    getConfirmation() {
      this.errorMessage = '';
      this.validationErrors = {};
      this.$validator.validate().then((valid) => {
        if (valid) {
          this.$passwordConfirmPluginModal.show({
            confirmCallback: this.savePhone,
          });
        }
      });
    },
    setPhoneObject(phoneObject) {
      this.phoneObject = phoneObject;
    },
    verifyPhone() {
      if (!this.phoneVerified) {
        this.$refs.verifyPhoneModal.show();
      }
    },
    onVerify() {
      this.fetchUser();
    },
    deletePhoneBtnAction() {
      this.$passwordConfirmPluginModal.show({
        confirmCallback: this.deletePhone,
      });
    },
  },
};
</script>
